/**
 * Keystone — Leverage Finder analyzer.
 *
 * Keystone reads the dependency graph the workspace has already drawn between its
 * items (item_relationship_edges, relationship_type = 'dependency') and finds the
 * thing no ranked list ever shows: the unglamorous item that silently unblocks a
 * large number of others.
 *
 *   keystone           A single item that, directly or transitively, blocks many
 *                      downstream priorities. Sequence it first and everything
 *                      behind it moves; ignore it and they all stall.
 *   dependency_cycle   A circular chain of blockers — an impossible sequence that
 *                      can never start, hiding in the graph.
 *
 * A directed dependency edge a → b means "a depends on b" (b is the prerequisite).
 * Leverage = the count of distinct items that transitively depend on a node.
 * The graph maths is exact; the LLM only narrates.
 *
 * The pure detection core lives in supabase/functions/_shared/intelligenceCores.ts
 * (shared with the ambient edge function); this file keeps the client-side data
 * loading and batch orchestration.
 */

import { getSupabaseClient } from '../supabaseClient';
import type { RunScope, BatchStats } from '../agentRunService';
import type { AgentSettings } from '../agentSettingsService';
import { createFinding, clearActiveFindings, type NewFinding } from '../agentFindingsService';
import { narrateFinding } from './llmIntelligenceService';
import {
  detectKeystoneFindings,
  parseAltitude,
  KEYSTONE_PERSONA,
  MAX_FINDINGS,
  type RelEdge as Edge,
  type ItemMeta,
} from '../../../supabase/functions/_shared/intelligenceCores';

export const KEYSTONE_AGENT_TYPE = 'keystone';

// Re-export so existing importers of this module keep working unchanged.
export { detectKeystoneFindings };

// ── Data loading ─────────────────────────────────────────────────────────────────

async function load(workspaceId: string): Promise<{ edges: Edge[]; meta: Map<string, ItemMeta> }> {
  const supabase = getSupabaseClient();

  const { data: edgeRows } = await supabase
    .from('item_relationship_edges')
    .select('source_item_id, target_item_id, directionality, status, relationship_type')
    .eq('workspace_id', workspaceId)
    .eq('relationship_type', 'dependency')
    .in('status', ['confirmed', 'active']);

  const edges: Edge[] = ((edgeRows ?? []) as Record<string, unknown>[]).map((r) => ({
    source: r.source_item_id as string,
    target: r.target_item_id as string,
    directed: (r.directionality as string) === 'directed',
  }));

  const ids = new Set<string>();
  for (const e of edges) { ids.add(e.source); ids.add(e.target); }

  const meta = new Map<string, ItemMeta>();
  if (ids.size) {
    const { data: itemRows } = await supabase
      .from('items')
      .select('id, title, altitude_level')
      .in('id', [...ids]);
    for (const r of (itemRows ?? []) as Record<string, unknown>[]) {
      meta.set(r.id as string, {
        title: (r.title as string) ?? '',
        altitude: parseAltitude(r.altitude_level),
      });
    }
  }

  return { edges, meta };
}

// ── Batch runner ─────────────────────────────────────────────────────────────────

export async function runKeystoneBatch(
  workspaceId: string,
  _scope: RunScope,
  config: AgentSettings,
  onProgress: (done: number, total: number) => void,
  signal: AbortSignal,
): Promise<BatchStats> {
  const stats: BatchStats = { processed: 0, proposed: 0, skipped: 0, errors: 0 };

  const { edges, meta } = await load(workspaceId);
  stats.processed = meta.size;
  if (edges.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  let drafts = detectKeystoneFindings(edges, meta);
  drafts = drafts.sort((a, b) => b.weight - a.weight).slice(0, MAX_FINDINGS);

  if (drafts.length === 0) {
    onProgress(1, 1);
    return stats;
  }

  await clearActiveFindings(workspaceId, KEYSTONE_AGENT_TYPE);

  const total = drafts.length;
  let done = 0;
  for (const draft of drafts) {
    if (signal.aborted) break;

    const narration = await narrateFinding({
      persona: KEYSTONE_PERSONA,
      findingKind: draft.findingKind,
      evidence: draft.base.data ?? {},
      systemPrompt: config.systemPrompt,
      model: config.model,
    });

    const finding: NewFinding = {
      ...draft.base,
      summary: narration?.summary || draft.fallbackSummary,
      detail: narration?.detail || draft.fallbackDetail,
    };

    const { error } = await createFinding(workspaceId, KEYSTONE_AGENT_TYPE, finding);
    if (error) stats.errors++; else stats.proposed++;

    done++;
    onProgress(done, total);
    await new Promise<void>((resolve) => setTimeout(resolve, 0));
  }

  return stats;
}
